import { FileText } from "lucide-react";
import { useState } from "react";
import { useIntl } from "react-intl";
import { AgentMarkdown } from "@/components/chat/AgentMarkdown";
import { ArrowOpenIcon } from "@/components/documents/icons";
import { Button } from "@/components/design/Button";
import { Disclosure } from "@/components/design/Disclosure";
import { useOpenDocument } from "@/lib/use-open-document";
import type { StudioDocument } from "./notebook-markdown";

/**
 * A document brought into the notebook: its name as a file card, its text (as the documents extraction read
 * it) one click away and read-only (Marvin, 2026-09-29). The text is context for the assistant, the card only
 * shows it; the file itself stays in the user's documents and opens from here.
 */
export function StudioDocumentCard({ document }: { document: StudioDocument }) {
  const intl = useIntl();
  const [error, setError] = useState<string | null>(null);
  const openDocument = useOpenDocument(setError);
  const open = intl.formatMessage({ id: "studio.document.open", defaultMessage: "Open the document" });
  return (
    <Disclosure
      title={
        <span className="flex min-w-0 items-center gap-2">
          <FileText aria-hidden="true" className="size-4 shrink-0 text-ink-muted" strokeWidth={1.6} />
          <span className="truncate">{document.name}</span>
        </span>
      }
      headerAction={
        <Button variant="icon" size="sm" aria-label={open} title={open} onClick={() => void openDocument(document.documentId)}>
          <ArrowOpenIcon />
        </Button>
      }
    >
      <div className="max-h-96 overflow-auto px-5 py-3 text-smycles-body leading-relaxed text-ink">
        <AgentMarkdown content={document.text} />
      </div>
      {error ? <p className="px-5 pb-3 text-smycles-label text-danger">{error}</p> : null}
    </Disclosure>
  );
}
